import React, { useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet } from 'react-native';
import { useAccounts, useTransactions, useLiabilities } from '../hooks/useData';
import LoadingState from '../components/common/LoadingState';
import ErrorState from '../components/common/ErrorState';

const typeIcons = {
  checking: '🏦',
  savings: '💰',
  brokerage: '📈',
  retirement: '🪙',
  crypto: '🪙',
};

const typeLabels = {
  checking: 'Checking Account',
  savings: 'Savings Account',
  brokerage: 'Brokerage Account',
  retirement: 'Retirement Account',
  crypto: 'Crypto Portfolio',
};

const calculateRecentActivity = (transactions, accountId, days = 30) => {
  if (!transactions?.length) return { count: 0, total: 0 };
  
  const cutoffDate = new Date();
  cutoffDate.setDate(cutoffDate.getDate() - days);
  const cutoffString = cutoffDate.toISOString().split('T')[0];
  
  const recentTransactions = transactions.filter(tx => 
    tx.account_id === accountId && tx.date >= cutoffString
  );
  
  const total = recentTransactions.reduce((sum, tx) => sum + tx.amount, 0);
  
  return {
    count: recentTransactions.length,
    total: Math.round(total * 100) / 100
  };
};

const getAccountHealth = (balance, recentActivity) => {
  if (balance < 0) return { status: 'Critical', color: '#DC2626', backgroundColor: '#FEF2F2' };
  if (balance < 500 && recentActivity < 0) return { status: 'Warning', color: '#D97706', backgroundColor: '#FFFBEB' };
  if (recentActivity > 0) return { status: 'Growing', color: '#059669', backgroundColor: '#F0FDF4' };
  return { status: 'Stable', color: '#2563EB', backgroundColor: '#EFF6FF' };
};

const AccountCard = ({ account, recentActivity, liabilities = [] }) => {
  const health = getAccountHealth(account.balance, recentActivity.total);
  
  // Check if this is actually a liability (debt account)
  const isDebt = account.balance < 0 || liabilities.some(liab => liab.id === account.id);
  
  return (
    <View style={styles.accountCard}>
      <View style={styles.accountHeader}>
        <View style={styles.accountIconContainer}>
          <Text style={styles.accountIcon}>{typeIcons[account.type] || '💼'}</Text>
          <View style={styles.accountTitleContainer}>
            <Text style={styles.accountName}>{account.name}</Text>
            <Text style={styles.accountDetails}>
              {typeLabels[account.type]} • {account.institution}
            </Text>
          </View>
        </View>
        <View style={[
          styles.healthBadge,
          { backgroundColor: health.backgroundColor }
        ]}>
          <Text style={[styles.healthText, { color: health.color }]}>
            {health.status}
          </Text>
        </View>
      </View>
      
      <View style={styles.accountBody}>
        <View style={styles.balanceContainer}>
          <Text style={styles.balanceLabel}>Current Balance</Text>
          <Text style={[
            styles.balanceAmount,
            account.balance < 0 ? styles.negativeBalance : styles.positiveBalance
          ]}>
            {account.balance < 0 ? '-' : ''}${Math.abs(account.balance).toLocaleString()}
          </Text>
        </View>
        
        <View style={styles.activityGrid}>
          <View style={styles.activityItem}>
            <Text style={styles.activityLabel}>30-Day Activity</Text>
            <Text style={[
              styles.activityValue,
              recentActivity.total >= 0 ? styles.positiveActivity : styles.negativeActivity
            ]}>
              {recentActivity.total >= 0 ? '+' : ''}${recentActivity.total.toLocaleString()}
            </Text>
          </View>
          <View style={styles.activityItem}>
            <Text style={styles.activityLabel}>Transactions</Text>
            <Text style={styles.activityValue}>{recentActivity.count}</Text>
          </View>
        </View>
      </View>
      
      <View style={styles.accountActions}>
        <TouchableOpacity style={styles.primaryButton}>
          <Text style={styles.primaryButtonText}>View Details</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.secondaryButton}>
          <Text style={styles.secondaryButtonText}>Transfer</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const Accounts = () => {
  const { data: accounts, loading: accountsLoading, error: accountsError, refresh: refreshAccounts } = useAccounts();
  const { data: transactions, loading: transactionsLoading, error: transactionsError, refresh: refreshTransactions } = useTransactions();
  const { data: liabilities, loading: liabilitiesLoading, error: liabilitiesError, refresh: refreshLiabilities } = useLiabilities();
  
  const [filter, setFilter] = useState('All');
  const [sortBy, setSortBy] = useState('balance');

  const loading = accountsLoading || transactionsLoading || liabilitiesLoading;
  const error = accountsError || transactionsError || liabilitiesError;
  const refresh = () => {
    refreshAccounts();
    refreshTransactions();
    refreshLiabilities();
  };

  if (loading) return <LoadingState message="Loading accounts..." />;
  if (error) return <ErrorState message={error} onRetry={refresh} />;
  if (!accounts || !transactions) return null;

  // Calculate metrics
  const totalAssets = accounts.reduce((sum, acc) => sum + Math.max(0, acc.balance), 0);
  const totalDebt = Math.abs((liabilities || []).reduce((sum, liab) => sum + liab.balance, 0));
  const netWorth = totalAssets - totalDebt;

  // Combine accounts and liabilities for unified view
  const allAccounts = [
    ...accounts,
    ...(liabilities || []).map(liab => ({
      ...liab,
      id: liab.id || `liab-${liab.type}`,
      name: liab.name || `${liab.type.replace('_', ' ')} - ${liab.institution}`,
      type: liab.type,
      institution: liab.institution,
      balance: -liab.balance // Show as negative for debt
    }))
  ];

  // Filter accounts
  const filteredAccounts = allAccounts.filter(account => {
    if (filter === 'All') return true;
    if (filter === 'Assets') return account.balance > 0;
    if (filter === 'Debt') return account.balance < 0;
    if (filter === account.type) return true;
    return false;
  });

  // Sort accounts
  const sortedAccounts = [...filteredAccounts].sort((a, b) => {
    switch (sortBy) {
      case 'balance':
        return Math.abs(b.balance) - Math.abs(a.balance);
      case 'name':
        return a.name.localeCompare(b.name);
      case 'type':
        return a.type.localeCompare(b.type);
      case 'institution':
        return a.institution.localeCompare(b.institution);
      default:
        return 0;
    }
  });

  const accountTypes = [...new Set(allAccounts.map(acc => acc.type))];
  const filters = ['All', 'Assets', 'Debt', ...accountTypes];

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>Accounts</Text>
        <TouchableOpacity style={styles.addButton}>
          <Text style={styles.addButtonText}>+ Add Account</Text>
        </TouchableOpacity>
      </View>

      {/* Summary Cards */}
      <View style={styles.summaryGrid}>
        <View style={[styles.summaryCard, styles.assetsCard]}>
          <Text style={styles.summaryLabel}>Total Assets</Text>
          <Text style={styles.assetsAmount}>${totalAssets.toLocaleString()}</Text>
          <Text style={styles.summarySubtext}>
            {accounts.filter(acc => acc.balance > 0).length} accounts
          </Text>
        </View>
        
        <View style={[styles.summaryCard, styles.debtCard]}>
          <Text style={styles.summaryLabel}>Total Debt</Text>
          <Text style={styles.debtAmount}>
            {totalDebt > 0 ? '-' : ''}${totalDebt.toLocaleString()}
          </Text>
          <Text style={styles.summarySubtext}>
            {(liabilities || []).length} accounts
          </Text>
        </View>
        
        <View style={[styles.summaryCard, styles.netWorthCard]}>
          <Text style={styles.summaryLabel}>Net Worth</Text>
          <Text style={[
            styles.netWorthAmount,
            netWorth >= 0 ? styles.positiveNetWorth : styles.negativeNetWorth
          ]}>
            {netWorth >= 0 ? '$' : '-$'}{Math.abs(netWorth).toLocaleString()}
          </Text>
          <Text style={styles.summarySubtext}>
            {allAccounts.length} total accounts
          </Text>
        </View>
      </View>

      {/* Filters and Sort */}
      <View style={styles.controlsContainer}>
        <View style={styles.filtersContainer}>
          <Text style={styles.controlLabel}>Filter:</Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.filterScrollView}>
            {filters.map((f) => (
              <TouchableOpacity
                key={f}
                style={[
                  styles.filterButton,
                  filter === f ? styles.filterButtonActive : styles.filterButtonInactive
                ]}
                onPress={() => setFilter(f)}
              >
                <Text style={[
                  styles.filterButtonText,
                  filter === f ? styles.filterButtonTextActive : styles.filterButtonTextInactive
                ]}>
                  {f.replace('_', ' ')}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>

        <View style={styles.sortContainer}>
          <Text style={styles.controlLabel}>Sort by:</Text>
          <View style={styles.sortButtons}>
            {['balance', 'name', 'type', 'institution'].map((option) => (
              <TouchableOpacity
                key={option}
                style={[
                  styles.sortButton,
                  sortBy === option ? styles.sortButtonActive : styles.sortButtonInactive
                ]}
                onPress={() => setSortBy(option)}
              >
                <Text style={[
                  styles.sortButtonText,
                  sortBy === option ? styles.sortButtonTextActive : styles.sortButtonTextInactive
                ]}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
      </View>

      {/* Accounts Grid */}
      <View style={styles.accountsGrid}>
        {sortedAccounts.map((account) => {
          const recentActivity = calculateRecentActivity(transactions, account.id);
          return (
            <AccountCard
              key={account.id || account.name}
              account={account}
              recentActivity={recentActivity}
              liabilities={liabilities || []}
            />
          );
        })}
      </View>

      {/* Empty State */}
      {sortedAccounts.length === 0 && (
        <View style={styles.emptyState}>
          <Text style={styles.emptyStateTitle}>No accounts found</Text>
          <Text style={styles.emptyStateSubtitle}>Try adjusting your filters or add a new account</Text>
        </View>
      )}

      {/* Account Insights */}
      <View style={styles.insightsCard}>
        <Text style={styles.cardTitle}>Account Insights</Text>
        <View style={styles.insightsList}>
          <View style={styles.insightItem}>
            <Text style={styles.insightIcon}>💡</Text>
            <Text style={styles.insightText}>
              Your net worth is ${Math.abs(netWorth).toLocaleString()}
              {netWorth >= 0 ? '. Great job building wealth!' : '. Focus on paying down debt to improve your financial position.'}
            </Text>
          </View>
          <View style={styles.insightItem}>
            <Text style={styles.insightIcon}>📊</Text>
            <Text style={styles.insightText}>
              You have {accounts.length} asset accounts and {(liabilities || []).length} debt accounts. 
              {totalDebt > 0 ? ' Consider debt consolidation strategies to reduce interest payments.' : ' Keep building your assets!'}
            </Text>
          </View>
          <View style={styles.insightItem}>
            <Text style={styles.insightIcon}>🎯</Text>
            <Text style={styles.insightText}>
              Your largest asset is {accounts.reduce((max, acc) => acc.balance > max.balance ? acc : max, accounts[0])?.name || 'N/A'}.
              {totalAssets > 50000 ? ' Consider diversifying your portfolio.' : ' Keep growing your emergency fund.'}
            </Text>
          </View>
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9FAFB',
  },
  content: {
    maxWidth: 1200,
    width: '100%',
    alignSelf: 'center',
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
  },
  addButton: {
    backgroundColor: '#2563EB',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },

  // Summary Cards
  summaryGrid: {
    flexDirection: 'row',
    gap: 24,
    marginBottom: 32,
    // Stack on mobile
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      gap: 12,
    },
  },
  summaryCard: {
    flex: 1,
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  assetsCard: {
    backgroundColor: '#F0FDF4',
  },
  debtCard: {
    backgroundColor: '#FEF2F2',
  },
  netWorthCard: {
    backgroundColor: '#EFF6FF',
  },
  summaryLabel: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 8,
  },
  assetsAmount: {
    fontSize: 30,
    fontWeight: 'bold',
    color: '#059669',
  },
  debtAmount: {
    fontSize: 30,
    fontWeight: 'bold',
    color: '#DC2626',
  },
  netWorthAmount: {
    fontSize: 30,
    fontWeight: 'bold',
  },
  positiveNetWorth: {
    color: '#2563EB',
  },
  negativeNetWorth: {
    color: '#DC2626',
  },
  summarySubtext: {
    fontSize: 14,
    color: '#6B7280',
    marginTop: 4,
  },

  // Controls
  controlsContainer: {
    marginBottom: 32,
    gap: 16,
  },
  filtersContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    // Stack on mobile
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 12,
    },
  },
  sortContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    // Stack on mobile
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 12,
    },
  },
  controlLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    minWidth: 60,
    // Mobile adjustments
    '@media (max-width: 768px)': {
      minWidth: 0,
      fontSize: 16,
    },
  },
  filterScrollView: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  filterButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    borderWidth: 1,
    marginRight: 0,
    // Better mobile touch targets
    minHeight: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  filterButtonActive: {
    backgroundColor: '#2563EB',
    borderColor: '#2563EB',
  },
  filterButtonInactive: {
    backgroundColor: '#FFFFFF',
    borderColor: '#E5E7EB',
  },
  filterButtonText: {
    fontSize: 14,
    fontWeight: '500',
    // Mobile typography
    '@media (max-width: 768px)': {
      fontSize: 16,
    },
  },
  filterButtonTextActive: {
    color: '#FFFFFF',
  },
  filterButtonTextInactive: {
    color: '#374151',
  },
  sortButtons: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  sortButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    // Better mobile touch targets
    minHeight: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  sortButtonActive: {
    backgroundColor: '#2563EB',
    borderColor: '#2563EB',
  },
  sortButtonInactive: {
    backgroundColor: '#FFFFFF',
    borderColor: '#E5E7EB',
  },
  sortButtonText: {
    fontSize: 14,
    fontWeight: '500',
    // Mobile typography
    '@media (max-width: 768px)': {
      fontSize: 16,
    },
  },
  sortButtonTextActive: {
    color: '#FFFFFF',
  },
  sortButtonTextInactive: {
    color: '#374151',
  },

  // Account Cards
  accountsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 24,
    marginBottom: 32,
    // Stack on mobile
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      gap: 16,
    },
  },
  accountCard: {
    width: '48%', // 2 columns on web
    minWidth: 300, // Minimum width
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
    // Full width on mobile
    '@media (max-width: 768px)': {
      width: '100%',
      minWidth: 0,
    },
  },
  accountHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  accountIconContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  accountIcon: {
    fontSize: 32,
    marginRight: 12,
  },
  accountTitleContainer: {
    flex: 1,
  },
  accountName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  accountDetails: {
    fontSize: 14,
    color: '#6B7280',
  },
  healthBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 16,
  },
  healthText: {
    fontSize: 12,
    fontWeight: '600',
  },
  accountBody: {
    marginBottom: 16,
  },
  balanceContainer: {
    marginBottom: 12,
  },
  balanceLabel: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 4,
  },
  balanceAmount: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  positiveBalance: {
    color: '#111827',
  },
  negativeBalance: {
    color: '#DC2626',
  },
  activityGrid: {
    flexDirection: 'row',
    gap: 16,
  },
  activityItem: {
    flex: 1,
  },
  activityLabel: {
    fontSize: 14,
    color: '#6B7280',
    marginBottom: 4,
  },
  activityValue: {
    fontSize: 16,
    fontWeight: '600',
  },
  positiveActivity: {
    color: '#059669',
  },
  negativeActivity: {
    color: '#DC2626',
  },
  accountActions: {
    flexDirection: 'row',
    gap: 8,
  },
  primaryButton: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#EFF6FF',
    borderRadius: 8,
    alignItems: 'center',
  },
  primaryButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#2563EB',
  },
  secondaryButton: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    alignItems: 'center',
  },
  secondaryButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6B7280',
  },

  // Empty State
  emptyState: {
    alignItems: 'center',
    paddingVertical: 48,
  },
  emptyStateTitle: {
    fontSize: 18,
    color: '#9CA3AF',
    marginBottom: 8,
  },
  emptyStateSubtitle: {
    fontSize: 14,
    color: '#6B7280',
  },

  // Insights
  insightsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingVertical: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
    marginBottom: 16,
  },
  insightsList: {
    gap: 12,
  },
  insightItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  },
  insightIcon: {
    fontSize: 18,
    color: '#2563EB',
  },
  insightText: {
    flex: 1,
    fontSize: 14,
    color: '#374151',
    lineHeight: 20,
  },
});

export default Accounts; 